import {
  keepPreviousData,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import { useDisplayErrorNotification } from 'hooks/useDisplayErrorNotification';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';
import { NoDataFoundError } from '../APIRequestError';
import { handleSentryErrors } from '../HandleSentryErrors';
import { useCurrentUserStore } from 'store/UserStore';
import { ZodTypeAny } from 'zod';

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  pages?: number;
  limit?: number;
  page?: number;
  nextPage: number;
  invalidData?: string[];
}

export interface PaginationParams {
  siteID?: string | string[];
  organizationID?: string;
  userID?: string;
  providerID?: string;
  patientID?: string;
  statusName?: string;
  page: number;
  pageSize: number;
  filters?: MRT_ColumnFiltersState;
  globalFilter?: string;
  sorting?: MRT_SortingState;
}

export const hasSiteID = (siteID?: string | string[]): boolean =>
  Array.isArray(siteID) ? siteID.length > 0 : !!siteID;

interface UsePaginatedQueryParams<
  T,
  TResult extends PaginatedResult<T> = PaginatedResult<T>
> {
  queryKey: any[];
  fetchFunction: (params: PaginationParams) => Promise<TResult | null>;
  isEntity?: (obj: any) => obj is T;
  functionName: string;
  params: PaginationParams;
  enabled: boolean;
  displayNotification?: boolean;
  validationSchema?: ZodTypeAny;
}

/**
 * Custom hook for handling paginated queries with error notifications.
 *
 * @param queryKey The key for the query.
 * @param fetchFunction The function to fetch paginated data.
 * @param isEntity Function to check if an object is of type T.
 * @param params Parameters for pagination.
 * @param functionName Name of the function being executed.
 * @param enabled Flag to enable/disable the query.
 *
 * @returns The result of the query with data of type T or null, and potential error of type Error.
 * @returns {PaginatedResult<T>, Error} : {data: T[], total: number, nextPage: number, invalidData?: string[]}
 */

export const usePaginatedQuery = <
  T,
  TResult extends PaginatedResult<T> = PaginatedResult<T>
>({
  queryKey,
  fetchFunction,
  isEntity,
  params,
  functionName,
  enabled,
  displayNotification = false,
  validationSchema,
}: UsePaginatedQueryParams<T, TResult>): UseQueryResult<TResult, Error> => {
  const { handleNoDataFound, handleInvalidData } =
    useDisplayErrorNotification();
  const user = useCurrentUserStore();

  const invalidDataIds: string[] = [];
  const invalidData: T[] = [];
  const validationErrors: any[] = [];

  return useQuery<TResult, Error>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKey,
    queryFn: async () => {
      const data = await fetchFunction(params);

      if (!data) {
        const error = new NoDataFoundError(
          `No data found for ${functionName}`,
          functionName,
          params
        );
        handleSentryErrors({
          error,
          functionName,
          params,
          displayNotification,
          handleNoDataFound,
          handleInvalidData,
          user,
        });
        return {
          data: [],
          total: 0,
          nextPage: 0,
          invalidData: [],
        } as TResult;
      }

      for (const entity of data.data) {
        if (!isEntity(entity)) {
          if (entity && 'id' in entity) {
            invalidData.push(entity);
            invalidDataIds.push((entity as { id: string }).id);
            if (validationSchema) {
              validationErrors.push({
                id: (entity as { id: string }).id,
                errors: validationSchema?.safeParse(entity).error,
              });
            }
          }
        }
      }

      return { ...data, invalidData: invalidDataIds } as TResult;
    },
    enabled: enabled,
    placeholderData: keepPreviousData,
    staleTime: 60000 * 60 * 3, // 3 hours
    retry: false,
  });
};
